"use client";

import { useEffect, useCallback } from "react";
import { useLocale, useTranslations } from "next-intl";
import TermsContent from "./TermsContent";
import BookingTermsContent from "./BookingTermsContent";

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
  lang?: "th" | "en";
  variant?: "website" | "training";
}

export default function TermsModal({
  isOpen,
  onClose,
  lang: langProp,
  variant = "website",
}: TermsModalProps) {
  const locale = useLocale();
  const t = useTranslations("common");
  const tForm = useTranslations("form");
  const termsLang = langProp ?? (locale === "en" ? "en" : "th");
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
          <h2 className="text-lg font-bold text-dark leading-snug max-w-[85%]">
            {variant === "training" ? tForm("consentFullText") : t("termsModalTitle")}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            aria-label={t("close")}
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4">
          {variant === "training" ? <BookingTermsContent lang={termsLang} /> : <TermsContent lang={termsLang} />}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 shrink-0 flex justify-center">
          <button
            onClick={onClose}
            className="px-8 py-2.5 bg-honda-red text-white rounded-lg hover:bg-honda-red-dark transition-colors font-medium min-w-[140px]"
          >
            {t("close")}
          </button>
        </div>
      </div>
    </div>
  );
}
